import React from 'react';
import { useLanguage } from '../../../context/LanguageContext';

const ExamGridOverlay = ({ showGrid, setShowGrid, isReviewMode, questions, answers, currentQuestionIndex, setCurrentQuestionIndex, mode, onDemandAnswers, isHistoryVuReview = false }) => {
    const { language } = useLanguage();

    if (!showGrid) return null;

    return (
        <div className="fixed inset-0 z-[210] bg-white/95 dark:bg-black/95 backdrop-blur-xl animate-in fade-in duration-300 p-6 md:p-8 flex flex-col text-gray-900 dark:text-white">
            <div className="flex justify-between items-center mb-8 max-w-4xl w-full mx-auto">
                <h2 className="text-2xl md:text-3xl font-bold">{language === 'fr' ? 'Navigation' : 'Navigation'}</h2>
                <button
                    onClick={() => setShowGrid(false)}
                    className="p-3 bg-gray-100 dark:bg-white/10 rounded-full hover:bg-gray-200 dark:hover:bg-white/20 transition"
                >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <div className="flex-1 overflow-y-auto pb-12 custom-scrollbar">
            <div className="grid grid-cols-10 md:grid-cols-15 lg:grid-cols-20 gap-1.5 md:gap-2 content-start max-w-4xl mx-auto w-full">
                {questions.map((_, idx) => {
                    const answerValue = answers[questions[idx].id];
                    // A question is "answered" if it has a real answer (not 0, not undefined)
                    const isAnswered = answerValue !== undefined && answerValue !== 0 && answerValue !== '0';
                    // A question is "seen but not answered" if value is 0
                    const isSeenNotAnswered = answerValue === 0 || answerValue === '0';
                    const isCurrent = currentQuestionIndex === idx;

                    let isDisabled = false;
                    if (mode === 'flash' && !isReviewMode) {
                        // Can go back to answered questions, can't jump ahead to unanswered
                        isDisabled = !isAnswered && idx !== currentQuestionIndex;
                    }

                    let baseClass = 'bg-white dark:bg-white/5 border-gray-200 dark:border-white/5 text-gray-500 hover:border-gray-300 dark:hover:border-white/30';

                    let isCorrect = false;
                    let isWrong = false;
                    let isStaleAnswer = false;
                    // Show correct/wrong in review mode AND flash mode
                    if (isReviewMode || mode === 'flash') {
                        const secret = onDemandAnswers[questions[idx].id];
                        const userAns = answers[questions[idx].id];
                        if (userAns !== undefined && userAns !== 0 && userAns !== '0' && secret) {
                            const answerExistsInOptions = questions[idx].options?.some(opt => String(opt.id) === String(userAns));
                            if (!answerExistsInOptions && isReviewMode) {
                                isStaleAnswer = true;
                            } else if (String(userAns) === String(secret.correctReponseId)) {
                                isCorrect = true;
                            } else {
                                isWrong = true;
                            }
                        }
                    }

                    // New question in vu-examen history review
                    const isNewVuQuestion = isHistoryVuReview && isReviewMode && answerValue === undefined;

                    if (isCurrent && isNewVuQuestion) {
                        baseClass = 'bg-amber-500 text-white border-amber-500 shadow-xl scale-110 ring-2 ring-amber-400';
                    } else if (isCurrent && isStaleAnswer) {
                        baseClass = 'bg-gray-500 text-white border-gray-500 shadow-xl scale-110 ring-2 ring-gray-400';
                    } else if (isCurrent) {
                        if (isCorrect || isWrong) {
                            baseClass = isCorrect
                                ? 'bg-emerald-700 text-white border-emerald-700 shadow-xl scale-110 ring-2 ring-emerald-400'
                                : 'bg-red-700 text-white border-red-700 shadow-xl scale-110 ring-2 ring-red-400';
                        } else if (isSeenNotAnswered) {
                            // Current question that was seen but not answered — orange highlight
                            baseClass = 'bg-orange-500 text-white border-orange-500 shadow-xl scale-110 ring-2 ring-orange-400';
                        } else {
                            baseClass = 'bg-gray-900 text-white dark:bg-white dark:text-black border-gray-900 dark:border-white scale-110 shadow-lg';
                        }
                    } else if (isNewVuQuestion) {
                        baseClass = 'bg-amber-50 dark:bg-amber-500/10 border-amber-300 dark:border-amber-500/30 text-amber-600 dark:text-amber-400 border-dashed';
                    } else if (isStaleAnswer) {
                        baseClass = 'bg-gray-300 dark:bg-white/10 text-gray-500 dark:text-gray-400 border-gray-300 dark:border-white/20';
                    } else if (isCorrect) {
                        baseClass = 'bg-emerald-500 text-white border-emerald-500';
                    } else if (isWrong) {
                        baseClass = 'bg-red-500 text-white border-red-500';
                    } else if (isReviewMode) {
                        baseClass = 'bg-gray-400 text-white border-gray-400';
                    } else if (isSeenNotAnswered && !isReviewMode) {
                        // Seen but not answered — orange indicator
                        baseClass = 'bg-orange-100 dark:bg-orange-500/20 border-orange-200 dark:border-orange-500/30 text-orange-700 dark:text-orange-400';
                    } else if (isAnswered && !isReviewMode) {
                        baseClass = 'bg-blue-100 dark:bg-white/20 border-blue-200 dark:border-white/20 text-blue-700 dark:text-white';
                    } else if (isDisabled) {
                        baseClass = 'opacity-20 cursor-not-allowed bg-gray-100 dark:bg-white/5 border-transparent';
                    }

                    return (
                        <button
                            key={idx}
                            disabled={isDisabled}
                            onClick={() => {
                                setCurrentQuestionIndex(idx);
                                setShowGrid(false);
                            }}
                            className={`aspect-square rounded-lg font-bold text-xs md:text-sm transition-all flex items-center justify-center border ${baseClass}`}
                        >
                            {idx + 1}
                        </button>
                    );
                })}
            </div>
            </div>
        </div>
    );
};

export default ExamGridOverlay;
